import { concatJsonFormKey } from './utils';
import { Button, Form } from 'antd';
import { NamePath } from 'antd/es/form/interface';
import Card, { CardProps } from 'antd/lib/card';
import { FormInstance, FormItemProps, FormProps, RuleObject } from 'antd/lib/form';
// import { FormListFieldData } from 'antd/lib/form/FormList';
import { ValidatorRule } from 'rc-field-form/lib/interface';
import React, { forwardRef, useImperativeHandle } from 'react';
import { IconPlus } from '@base/icon';
import { Row, Col, IRowProps } from '@base/lib/ThemeGrid';
// import { IconMinus } from '@base/icon';

type JsonFormFunctionRenderExtra = {
    parentKey?: NamePath;
    condition: boolean;
};
type UsedFormItemProps =
    | 'label'
    | 'name'
    | 'rules'
    | 'hidden'
    | 'valuePropName'
    | 'children'
    | 'initialValue';
type JsonFormItemLabel<Values = any> =
    | React.ReactNode
    | ((form: FormInstance<Values>, extra: JsonFormFunctionRenderExtra) => React.ReactNode);
type JsonFormItemRules<Values = any> =
    | RuleObject[]
    | ValidatorRule[]
    | ((
    form: FormInstance<Values>,
    extra: JsonFormFunctionRenderExtra,
) => RuleObject[] | ValidatorRule[]);
type JsonFormItemComponent<Values = any> =
    | React.ReactNode
    | ((form: FormInstance<Values>, extra: JsonFormFunctionRenderExtra) => React.ReactNode);
type JsonFormItemCondition<Values = any> =
    | boolean
    | ((form: FormInstance<Values>, parentKey?: NamePath) => boolean);
type JsonFormType = 'formArray' | 'formCard' | 'formGrid';
type JsonFormItemKeys = {
    itemKey?: NamePath;
    index?: number;
    fieldKey?: React.Key | React.Key[];
    actualKey?: NamePath;
    parentKey?: NamePath;
    parentHidden: boolean;
};

interface IFormInitialLength {
    /** 初始长度 */
    initialLength?: number;
}

interface IFormArrayExtraProps extends IFormInitialLength {
    /** formArray下包裹formitem的父节点样式 */
    childWrapperStyle?: React.CSSProperties;
}

interface IFormTabExtraProps extends IFormInitialLength {
    /** 显示的tab名 */
    tabName?: string;
    /** tab上展示额外的按钮 */
    tabBarExtraContent?: React.ReactNode | null;
}

export declare interface JsonFormConfig<Values = any> {
    /** 指定表单提交对应的key值 嵌套字段使用数组格式 */
    formKey?: NamePath;
    /**
     * 定制化渲染
     * formArray        可增删的表单组
     * formTab          tab类型的表单组
     * formCard         在同一card下面的表单组
     */
    type?: JsonFormType;
    /** 生成form.item对应的label属性 */
    label?: JsonFormItemLabel<Values>;
    /** 表单域 可以是antd的表单组件 也可以 自定义表单组件但必须遵循antd的自定义规则 */
    component?: JsonFormItemComponent<Values>;
    /** 在表单域前添加字符 */
    addonBefore?: React.ReactNode;
    /** 在表单域后添加字符 */
    addonAfter?: React.ReactNode;
    /** 校验器 */
    rules?: JsonFormItemRules<Values>;
    /** 自定义表单域的属性 和antd formItemProps一样 */
    formItemProps?: Omit<FormItemProps, UsedFormItemProps>;
    /** 当type有值的时候通过children收集表单域 */
    children?: JsonFormConfig<Values>[];
    /** 控制显示条件 */
    condition?: JsonFormItemCondition<Values>;
    /** 支持某些组件不通过value传值 例如check组件利用checked传值 */
    valuePropName?: string;
    /** 组件的默认值 */
    initialValue?: any;
    /** type不为空时针对该type扩展的属性 */
    typeExtraProps?: IFormArrayExtraProps | IFormTabExtraProps | CardProps | IRowProps;
}

export declare interface JsonFormProps<Values = any> {
    /** 经 Form.useForm() 创建的 form 控制实例 */
    form?: FormInstance<Values>;
    /** 初始化数据 */
    initialValues?: object;
    /** form的props 同antd formProps一样 */
    formProps?: Omit<FormProps<Values>, keyof JsonFormProps>;
    /** jsonform 配置 */
    config: JsonFormConfig<Values>[];
    /** 可以添加一些按钮组件 */
    extra?: React.ReactNode;
    /** 支持class component中使用ref的方式进行调用 */
    ref?:
        | ((instance: FormInstance<Values> | null) => void)
        | React.RefObject<FormInstance<Values>>
        | null;
    className?: string;
}

interface JsonFormStateConfig {
    [key: string]: string;
}

const JsonForm: React.FC<JsonFormProps> = forwardRef<FormInstance, JsonFormProps>(
    ({ config, extra, form, initialValues, className, formProps = {} }, ref) => {
        const [formInstance] = Form.useForm(form);

        const renderLabel = (
            label: JsonFormItemLabel,
            instance: FormInstance,
            extra: JsonFormFunctionRenderExtra,
        ) => {
            if (typeof label === 'function') return label(instance, extra);
            return label;
        };

        const renderRules = (
            rules: JsonFormItemRules | undefined,
            instance: FormInstance,
            extra: JsonFormFunctionRenderExtra,
        ) => {
            if (!extra.condition) return [];
            if (typeof rules === 'function') return rules(instance, extra);
            return rules;
        };

        const renderHidden = (
            condition: JsonFormItemCondition = true,
            instance: FormInstance,
            parentKey?: NamePath,
        ) => {
            if (typeof condition === 'function') return !condition(instance, parentKey);
            return !condition;
        };

        const renderComponent = (
            component: JsonFormItemComponent,
            instance: FormInstance,
            extra: JsonFormFunctionRenderExtra,
        ) => {
            if (typeof component === 'function') return component(instance, extra);
            return component;
        };

        const renderFormArray = (
            item: JsonFormConfig,
            itemProps: JsonFormItemKeys,
            instance: FormInstance,
        ) => {
            const { children = [], typeExtraProps = {}, rules, initialValue } = item;
            const { itemKey, parentKey, actualKey, parentHidden } = itemProps;
            if (!itemKey || !actualKey) {
                throw new Error("当JsonForm的type为'formArray'时，formKey不能为空");
            }
            if (initialValue !== undefined && !(initialValue instanceof Array)) {
                throw new Error("当JsonForm的type为'formArray'时，initialValue只能为Array类型");
            }

            const { childWrapperStyle, initialLength } = typeExtraProps as IFormArrayExtraProps;

            return (
                <Form.List
                    name={itemKey}
                    initialValue={initialValue || Array(initialLength).fill(undefined)}
                    rules={
                        renderRules(rules, instance, {
                            parentKey,
                            condition: !parentHidden,
                        }) as ValidatorRule[]
                    }
                >
                    {(fields, { add, remove, move }, { errors }) => (
                        <>
                            {fields.map(({ name, key, fieldKey }, index) => (
                                <div
                                    key={key}
                                    style={{
                                        backgroundColor: index % 2 === 1 ? 'transparent' : '#FAFAFA',
                                        paddingTop: '13px',
                                        ...childWrapperStyle,
                                    }}
                                >
                                    {children.map((child, childIndex) =>
                                        renderFormItem(child, {
                                            itemKey: concatJsonFormKey(name, child.formKey),
                                            fieldKey: concatJsonFormKey(fieldKey, child.formKey),
                                            index: childIndex,
                                            actualKey: concatJsonFormKey(
                                                itemKey,
                                                name,
                                                child.formKey,
                                            ),
                                            parentKey: concatJsonFormKey(itemKey, name),
                                            parentHidden,
                                        }),
                                    )}
                                    {fields.length > 1 && (
                                        <Form.Item style={{ display: 'inline-flex' }}>
                                            {index > 0 && (
                                                <a
                                                    style={{ marginLeft: 12 }}
                                                    className="ant-form-text"
                                                    onClick={() => move(index, index - 1)}
                                                >
                                                    上移
                                                </a>
                                            )}
                                            {index < fields.length - 1 && (
                                                <a
                                                    style={{ marginLeft: 12 }}
                                                    className="ant-form-text"
                                                    onClick={() => move(index, index + 1)}
                                                >
                                                    下移
                                                </a>
                                            )}
                                            <a
                                                style={{ marginLeft: 12 }}
                                                className="ant-form-text"
                                                onClick={() => remove(name)}
                                            >
                                                删除
                                            </a>
                                        </Form.Item>
                                    )}
                                </div>
                            ))}
                            <Button
                                type="dashed"
                                onClick={() => add()}
                                style={{ width: '100%', marginTop: '16px' }}
                            >
                                <IconPlus/>
                            </Button>
                        </>
                    )}
                </Form.List>
            );
        };

        const renderFormCard = (item: JsonFormConfig, itemProps: JsonFormItemKeys) => {
            const { children = [], typeExtraProps = {} } = item;
            const { itemKey, parentHidden } = itemProps;
            return (
                <Form.Item>
                    <Card {...(typeExtraProps as CardProps)}>
                        {children.map((one, childIndex) =>
                            renderFormItem(one, {
                                itemKey: concatJsonFormKey(itemKey, one.formKey),
                                index: childIndex,
                                actualKey: concatJsonFormKey(itemKey, one.formKey),
                                parentKey: concatJsonFormKey(itemKey),
                                parentHidden,
                            }),
                        )}
                    </Card>
                </Form.Item>
            );
        };

        const renderFormGrid = (item: JsonFormConfig, itemProps: JsonFormItemKeys) => {
            const { children = [], typeExtraProps = {} } = item;
            const { itemKey, parentHidden } = itemProps;
            return (
                <Form.Item>
                    <Row {...(typeExtraProps as IRowProps)}>
                        {children.map((one, childIndex) => (
                            <Col key={childIndex}>
                                {renderFormItem(one, {
                                    itemKey: concatJsonFormKey(itemKey, one.formKey),
                                    index: childIndex,
                                    actualKey: concatJsonFormKey(itemKey, one.formKey),
                                    parentKey: concatJsonFormKey(itemKey),
                                    parentHidden,
                                })}
                            </Col>
                        ))}
                    </Row>
                </Form.Item>
            );
        };

        function renderFormItem(item: JsonFormConfig, itemProps: JsonFormItemKeys) {
            const {
                type,
                label,
                condition,
                component,
                addonBefore,
                addonAfter,
                rules,
                valuePropName,
                initialValue,
                formItemProps = {},
            } = item;
            const { itemKey, fieldKey, index, actualKey, parentKey, parentHidden } = itemProps;
            const shouldUpdate = formItemProps.shouldUpdate
                ? formItemProps.shouldUpdate
                : [label, condition, rules, component].some(one => typeof one === 'function');
            const renderChildren = (instance: FormInstance) => {
                const conditionResult =
                    renderHidden(condition, instance, parentKey) || parentHidden;
                const extra: JsonFormFunctionRenderExtra = {
                    parentKey,
                    condition: !conditionResult,
                };
                const renderedRules = renderRules(rules, instance, extra) || [];
                const required =
                    formItemProps.required !== undefined
                        ? formItemProps.required
                        : renderedRules.some((rule: RuleObject) => {
                            if (rule && typeof rule === 'object' && rule.required) {
                                return true;
                            }
                            return false;
                        });
                let child: React.ReactNode = null;
                if (type === 'formArray') {
                    child = renderFormArray(item, itemProps, instance);
                } else if (type === 'formCard') {
                    child = renderFormCard(item, itemProps);
                } else if (type === 'formGrid') {
                    child = renderFormGrid(item, itemProps);
                } else {
                    child = (
                        <>
                            {addonBefore !== undefined && (
                                <span className="ant-form-text">{addonBefore}</span>
                            )}
                            <Form.Item
                                name={itemKey}
                                fieldKey={fieldKey}
                                noStyle
                                initialValue={initialValue}
                                valuePropName={valuePropName}
                                rules={renderedRules}
                            >
                                {renderComponent(component, instance, extra)}
                            </Form.Item>
                            {addonAfter !== undefined && (
                                <span className="ant-form-text" style={{ paddingLeft: 8 }}>
                                    {addonAfter}
                                </span>
                            )}
                        </>
                    );
                }
                return (
                    <Form.Item
                        {...formItemProps}
                        label={renderLabel(label, instance, extra)}
                        hidden={conditionResult}
                        required={required}
                    >
                        {child}
                    </Form.Item>
                );
            };
            return (
                <Form.Item
                    noStyle
                    shouldUpdate={shouldUpdate}
                    key={`${actualKey instanceof Array ? actualKey.join('.') : actualKey}-${index}`}
                >
                    {shouldUpdate ? renderChildren : renderChildren(formInstance)}
                </Form.Item>
            );
        }

        useImperativeHandle(ref, () => formInstance);
        return (
            <Form
                {...formProps}
                initialValues={initialValues}
                form={formInstance}
                className={className}
            >
                {config.map((item, index) =>
                    renderFormItem(item, {
                        itemKey: concatJsonFormKey(item.formKey),
                        actualKey: concatJsonFormKey(item.formKey),
                        parentKey: [],
                        index,
                        parentHidden: false,
                    }),
                )}
                {extra}
            </Form>
        );
    },
);

export default JsonForm;
